/**
 * 어드민 채팅 로그 — 토큰 칩 hover 툴팁 빌더.
 *
 * 칩 라벨 ("91.8K in / 357 out") 은 짧게 유지하고 정확한 숫자 + 부연은 툴팁으로.
 * 분기가 많아 (multi-iteration / 부분 커버리지 / 캐시 hit / 캐시 생성) 회귀가 잦을 수
 * 있어 순수 함수로 추출하고 단위 테스트로 보장.
 */

export type TokenSource = 'measured' | 'estimated' | 'mixed';

const SOURCE_LABEL: Record<TokenSource, string> = {
  measured: '실측 (LLM 응답의 usage)',
  estimated: '추정 (char heuristic — LLM 응답에 usage 가 없음)',
  mixed: '일부 실측, 일부 추정',
};

export interface TokenTooltipArgs {
  inputTokens: number;
  outputTokens: number;
  source: TokenSource;
  /** 합산에 포함된 LLM 콜 횟수. 1보다 크면 multi-iteration 안내 줄 추가. */
  llmCallCount?: number;
  /** 토큰 기록된 메시지 / 전체 assistant 메시지. 부분 커버리지면 경고 줄 추가. */
  coverage?: { measured: number; total: number };
  /** prompt cache hit (inputTokens 의 부분집합). 양수일 때만 노출. */
  cachedInputTokens?: number;
  /** 이번에 cache 에 쓰인 토큰 (Anthropic 만 양수). */
  cacheCreationTokens?: number;
}

/**
 * 줄바꿈으로 구분된 멀티라인 툴팁 문자열을 반환. `title` 속성에 그대로 set.
 * - inputTokens 가 0 이면 hit rate 표시 안 함 (DIV/0 회피)
 * - hit rate 가 inputTokens 범위를 벗어나도 100% 로 클램프 (방어)
 */
export function buildTokenTooltip(args: TokenTooltipArgs): string {
  const inStr = args.inputTokens.toLocaleString();
  const outStr = args.outputTokens.toLocaleString();
  const lines: string[] = [`${inStr} in / ${outStr} out`, SOURCE_LABEL[args.source]];

  if (args.cachedInputTokens != null && args.cachedInputTokens > 0) {
    const cacheStr = args.cachedInputTokens.toLocaleString();
    const hitRate =
      args.inputTokens > 0
        ? ` (${Math.min(100, Math.round((args.cachedInputTokens / args.inputTokens) * 100))}%)`
        : '';
    lines.push(`캐시 hit: ${cacheStr}${hitRate} · 일반 input 대비 할인 청구`);
  }

  if (args.cacheCreationTokens != null && args.cacheCreationTokens > 0) {
    lines.push(
      `캐시 생성: ${args.cacheCreationTokens.toLocaleString()} · 일반 input 보다 비싸게 청구 (Anthropic)`,
    );
  }

  if (args.llmCallCount != null && args.llmCallCount > 1) {
    lines.push(`${args.llmCallCount}회의 LLM 콜 누적 (system + history 가 매 콜마다 카운트됨)`);
  }

  if (args.coverage && args.coverage.total > 0 && args.coverage.measured < args.coverage.total) {
    const missing = args.coverage.total - args.coverage.measured;
    lines.push(
      `${args.coverage.measured}/${args.coverage.total}개 메시지 기록, ${missing}개 미기록 (이전 메시지 또는 기록 누락)`,
    );
  }

  return lines.join('\n');
}
